import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { lazy, Suspense, useEffect } from "react";
import { Route, Switch, Redirect } from "react-router";
import ErrorBoundary from "./components/ErrorBoundary";
import { useAppDispatch } from "./utils/hooks";
import { loadSettings } from "./redux/slices/settingsSlice";
import Footer from "./components/Footer";
import { localeData } from "./data/locale";
import Header from "./components/Header";
import fetchIntercept from "fetch-intercept";
// images
import headerLogo from "./images/cont-logo.png";
import footerLogo from "./images/cont-footer-logo.png";
import heroBg from "./images/webp/hero-bg-1.webp";
import heroBg2x from "./images/webp/hero-bg-1@2x.webp";
import heroLogo from "./images/hero-logo-1.png";
import aboutImg from "./images/cont-about.png";
import aboutImgWebp from "./images/webp/cont-about.webp";
import sanitation from "./images/sanitation.png";
import water from "./images/water.png";
import electricity from "./images/electricity.png";
import well from "./images/well.png";
import roads from "./images/roads.png";
import { localeTo } from "./utils/helpers";
// pages
const Landing = lazy(() => import("./pages/Landing"));
const News = lazy(() => import("./pages/News"));
const Works = lazy(() => import("./pages/Works"));
const Jobs = lazy(() => import("./pages/Jobs"));

const headerNav = [
  {
    path: "/contracting",
    exact: true,
    name: localeData.home,
  },
  {
    path: "/contracting#about",
    exact: false,
    name: localeData.about,
  },
  {
    path: "/contracting#news",
    exact: false,
    name: localeData.news,
  },
  {
    path: "/contracting#works",
    exact: false,
    name: localeData.ourWork,
  },
  {
    path: "/contracting#partners",
    exact: false,
    name: localeData.partners,
  },
  {
    path: "/contracting#certificates",
    exact: false,
    name: localeData.certificates,
  },
  {
    path: "/contracting#branches",
    exact: false,
    name: localeData.branches,
  },
  {
    path: "/contracting/jobs",
    exact: false,
    name: localeData.jobs,
  },
];

const footerNav = [
  {
    path: "/contracting",
    name: localeData.cont,
  },
  {
    path: "/equipments",
    name: localeData.equipments,
  },
  {
    path: "/transport",
    name: localeData.transport,
  },
  {
    path: "/contracting",
    name: localeData.home,
  },
  {
    path: "/contracting#about",
    name: localeData.about,
  },
  {
    path: "/contracting#news",
    name: localeData.news,
  },
  {
    path: "/contracting#works",
    name: localeData.ourWork,
  },
  {
    path: "/contracting#branches",
    name: localeData.branches,
  },
  {
    path: "/contracting/jobs",
    name: localeData.jobs,
  },
];

function Contracting() {
  const dispatch = useAppDispatch();

  // mount logic
  useEffect(() => {
    // add Website-Key header to all fetch requests
    const unregister = fetchIntercept.register({
      request(url, config = {}) {
        config.headers = {
          ...config.headers,
          "Website-Key": "satc_contracting",
        };
        return [url, config];
      },
    });
    dispatch(loadSettings());
    document.title = localeData.cont;
    return () => unregister();
  }, []); // eslint-disable-line

  return (
    <>
      <Header logo={headerLogo} nav={headerNav} />
      <Suspense
        fallback={
          <div className="text-center page-placeholder">
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: "28px" }} />}
            />
          </div>
        }
      >
        <Switch>
          <Route path="/contracting" exact>
            <ErrorBoundary>
              <Landing
                heroBg={heroBg}
                heroBg2x={heroBg2x}
                heroLogo={heroLogo}
                heroSlogan={localeData.heroHeading}
                aboutImg={aboutImg}
                aboutImgWebp={aboutImgWebp}
                aboutImgW="655"
                aboutImgH="578"
                worksTitle={localeData.ourWork}
                works={[
                  {
                    to: "/contracting/works",
                    src: sanitation,
                    title: localeData.sanitation,
                  },
                  {
                    to: "/contracting/works#project-4",
                    src: water,
                    title: localeData.water,
                  },
                  {
                    to: "/contracting/works#project-3",
                    src: electricity,
                    title: localeData.electricity,
                  },
                  {
                    to: "/contracting/works#project-2",
                    src: well,
                    title: localeData.well,
                  },
                  {
                    to: "/contracting/works#project-1",
                    src: roads,
                    title: localeData.roads,
                  },
                ]}
              />
            </ErrorBoundary>
          </Route>
          <Route path="/contracting/news/:id">
            <ErrorBoundary>
              <News siteName={localeData.cont} />
            </ErrorBoundary>
          </Route>
          <Route path="/contracting/works">
            <ErrorBoundary>
              <Works />
            </ErrorBoundary>
          </Route>
          <Route path="/contracting/jobs">
            <ErrorBoundary>
              <Jobs />
            </ErrorBoundary>
          </Route>
          <Redirect to={localeTo("/404")} />
        </Switch>
      </Suspense>
      <Footer logo={footerLogo} nav={footerNav} />
    </>
  );
}

export default Contracting;
